import { PayloadAction, createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import { cacheService, orderService, setupService } from "../../Core/Api/services";
import { RootState } from "../redux-store";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { DbCultureInfos } from "../../Core/Api/Types/db-culture-infos";
import { EnrichedMetadata } from "../../Core/Api/Types/enriched-metadata";
import { DbDeliveryBroadcasterInfos } from "../../Core/Api/Types/db-delivery-broadcaster-infos";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { ObjectStatus } from "../../Core/Api/Types/object-status";

export const GetBroadcasters: any = createAsyncThunk("bundlemaker/GetBroadcasterList", async () => {
  return await orderService.GetBroadcastersBundlemakerList();
});

export const GetCultures: any = createAsyncThunk("bundlemaker/GetCultures", async () => {
  return await setupService.GetAllCultures();
});

export interface ReduxStateBundleMaker {
  bundleId: number;
  selectedTitle: DbAssetObjectInfos | null;
  packageList: DbAssetObjectInfos[];
  selectedPackageIndex: number;
  selectedEpisode: number | null;
  packageBroadcasterId: number;
  packageCultures: DbCultureInfos[];
  titleCultures: DbCultureInfos[];
  titleSelectedCulture: DbCultureInfos | null;
  selectedCultureIndex: number;
  metadata: EnrichedMetadata[];
  metadataMap: any;
  broadcasters: DbDeliveryBroadcasterInfos[];
  cultures: DbCultureInfos[];
  artworkMeta: any[];
  artworkContentId: number;
  editTitle: boolean;
  titleLocalizedMeta: EnrichedMetadata[];
  statusMap: any;
  isRightPanelOpen: boolean;
  openTitleId: number;
  fieldUpdate: { field: string; count: number };
}

const initialState: ReduxStateBundleMaker = {
  selectedTitle: null,
  bundleId: -1,
  packageList: [],
  selectedPackageIndex: -1,
  selectedEpisode: null,
  packageBroadcasterId: -1,
  packageCultures: [],
  titleCultures: [],
  titleSelectedCulture: null,
  selectedCultureIndex: -1,
  metadata: [],
  metadataMap: {},
  broadcasters: [],
  cultures: [],
  artworkMeta: [],
  artworkContentId: -1,
  titleLocalizedMeta: [],
  editTitle: false,
  statusMap: {},
  isRightPanelOpen: false,
  openTitleId: -1,
  fieldUpdate: { field: "", count: 0 },
};

export const bundleMakerSlice = createSlice({
  name: "bundlemaker",
  initialState,
  reducers: {
    SetOpenTitleId: (state, action) => {
      state.openTitleId = action.payload;
    },
    SetBundleId: (state, action: PayloadAction<number>) => {
      state.bundleId = action.payload;
    },
    SetPackageList: (state, action: PayloadAction<DbAssetObjectInfos[]>) => {
      state.packageList = action.payload;
      for (let i = 0; i < action.payload.length; i++) {
        state.statusMap[action.payload[i].id] = false;
      }
    },
    SetSelectedPackageIndex: (state, action: PayloadAction<number>) => {
      state.selectedPackageIndex = action.payload;
    },
    SetPackageBroadcasterId: (state, action: PayloadAction<number>) => {
      state.packageBroadcasterId = action.payload;
    },
    SetCurrentPackageStatus: (state, action: PayloadAction<ObjectStatus>) => {
      const currentPackage = state.packageList[state.selectedPackageIndex];
      if (currentPackage) {
        currentPackage.status = action.payload;
        state.statusMap[currentPackage.id] = true;
      }
    },
    SetPackageCultures: (state, action: PayloadAction<DbCultureInfos[]>) => {
      state.packageCultures = action.payload;
      if (action.payload && action.payload.length > 0) {
        state.selectedCultureIndex = 0;
      }
    },
    SetTitleCultures: (state, action: PayloadAction<DbCultureInfos[]>) => {
      state.titleCultures = action.payload;
      if (state.selectedTitle?.id) state.openTitleId = state.selectedTitle.id;
    },
    SetSelectedCultureIndex: (state, action: PayloadAction<number>) => {
      state.selectedCultureIndex = action.payload;
    },
    SetSelectedEpisode: (state, action: PayloadAction<number | null>) => {
      state.selectedEpisode = action.payload;
    },
    SetTitleSelectedCulture: (state, action: PayloadAction<DbCultureInfos>) => {
      state.titleSelectedCulture = action.payload;
    },
    SetSelectedTitle: (state, action: PayloadAction<DbAssetObjectInfos>) => {
      state.selectedTitle = action.payload;
      state.statusMap[action.payload.id] = false;
    },
    SetMetadata: (state, action: PayloadAction<EnrichedMetadata[]>) => {
      state.metadata = action.payload;
    },
    SetBroadcasters: (state, action: PayloadAction<DbDeliveryBroadcasterInfos[]>) => {
      state.broadcasters = action.payload;
    },
    SetCultures: (state, action: PayloadAction<DbCultureInfos[]>) => {
      state.cultures = action.payload;
      state.selectedCultureIndex = 0;
    },
    SetArtworks: (state, action: PayloadAction<DbAssetObjectInfos[]>) => {
      state.artworkMeta = action.payload;
    },
    SetArtworkContentId: (state, action: PayloadAction<number>) => {
      state.artworkContentId = action.payload;
    },
    SetEditTitle: (state, action: PayloadAction<boolean>) => {
      state.editTitle = action.payload;
    },
    SetStatusMap: (state, action: PayloadAction<{ modified: boolean; id: number }>) => {
      const { modified, id } = action.payload;
      state.statusMap[id] = modified;
    },
    SetIsRightPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isRightPanelOpen = action.payload;
    },
    SetTitleLocalizedMeta: (state, action: PayloadAction<EnrichedMetadata[]>) => {
      state.titleLocalizedMeta = action.payload;
    },
    UpdateTitleLocalizedMeta: (
      state,
      action: PayloadAction<{
        id: number;
        value: any;
        valueIndex?: number;
        pushAndReplaceWhereCultureIds?: number[];
      }>
    ) => {
      const { id, value, valueIndex, pushAndReplaceWhereCultureIds } = action.payload;
      let match = state.titleLocalizedMeta.find((x) => x.field?.id === id)!.value!;

      if (valueIndex === undefined) {
        if (pushAndReplaceWhereCultureIds === undefined) match.value = value;
        else {
          // Considering the "cultureId" prop never changes
          match.value = match.value.filter(
            (x) => !pushAndReplaceWhereCultureIds.includes(x.cultureId) || x.epNb !== state.selectedEpisode
          );
          value.forEach((element) => {
            match.value.push(element);
          });
        }
      } else
        match.value[valueIndex] = {
          $type: convertTypeToValueType(match.fieldType!),
          ...value,
          epNb: state.selectedEpisode,
          cultureId: state.titleSelectedCulture?.id ?? GlobalConst.WORLD_CULTURE_ID,
          language: state.titleSelectedCulture?.languages?.find((x) => x.isMain === true)?.isoCodeLocalized,
        };

      if (state.selectedTitle?.id) state.statusMap[state.selectedTitle.id] = true;
    },
    RemoveArtwork: (state, action: PayloadAction<number>) => {
      state.artworkMeta = state.artworkMeta.filter((x) => x.id !== action.payload);
    },

    AddCultures: (state, action: PayloadAction<DbCultureInfos[]>) => {
      state.packageCultures = [...state.packageCultures, ...action.payload];
      state.selectedCultureIndex = state.packageCultures.length - 1;
      state.statusMap[state.packageList[state.selectedPackageIndex].id] = true;
    },
    AddTitleCultures: (state, action: PayloadAction<DbCultureInfos[]>) => {
      state.titleCultures = [...state.titleCultures, ...action.payload];
      if (state.selectedTitle) state.statusMap[state.selectedTitle.id] = true;
    },
    AddArtwork: (state, action: PayloadAction<DbAssetObjectInfos>) => {
      state.artworkMeta = [...state.artworkMeta, action.payload];
    },
    RemoveCulture: (state, action: PayloadAction<number>) => {
      if (
        action.payload <= state.selectedCultureIndex &&
        state.packageCultures.length - 1 > 0 &&
        state.selectedCultureIndex > 0
      )
        state.selectedCultureIndex = state.selectedCultureIndex - 1;

      const removedCultureId = state.packageCultures[action.payload].id;
      state.packageCultures.splice(action.payload, 1);
      state.metadata = state.metadata.map(
        (x) =>
          ({
            ...x,
            value: {
              ...x.value,
              value:
                x.value?.value && x.value?.value.length > 0
                  ? x.value?.value.filter(
                      (v) =>
                        v.languageLocalizedCultureId !== removedCultureId &&
                        v.cultureId !== removedCultureId &&
                        !(v.system && v.system.cultureId === removedCultureId)
                    )
                  : x.value?.value,
            },
          } as EnrichedMetadata)
      );
      state.statusMap[state.packageList[state.selectedPackageIndex].id] = true;
    },
    RemoveTitleCulture: (state, action: PayloadAction<number>) => {
      state.titleCultures = state.titleCultures.filter((x) => x.id !== action.payload);
      state.titleLocalizedMeta = state.titleLocalizedMeta.map(
        (x) =>
          ({
            ...x,
            value: {
              ...x.value,
              value:
                x.value?.value && x.value?.value.length > 0
                  ? x.value?.value.filter(
                      (v) =>
                        v.languageLocalizedCultureId !== action.payload &&
                        v.cultureId !== action.payload &&
                        !(v.system && v.system.cultureId === action.payload)
                    )
                  : x.value?.value,
            },
          } as EnrichedMetadata)
      );
      if (state.selectedTitle?.id) state.statusMap[state.selectedTitle.id] = true;
    },
    SetMetadataMap: (state, action: PayloadAction<{ packageId: number; metadata: EnrichedMetadata[] }>) => {
      const { packageId, metadata } = action.payload;
      state.metadataMap[packageId] = metadata;
      if (state.packageList[state.selectedPackageIndex]?.id === packageId && state.metadata.length === 0)
        state.metadata = metadata;
    },

    AddPackage: (state, action: PayloadAction<DbAssetObjectInfos>) => {
      state.packageList = [...state.packageList, action.payload].sort((a, b) => b.id - a.id);
      state.selectedPackageIndex = state.packageList.findIndex((x) => x.id === action.payload.id);
      state.statusMap[action.payload.id] = false;
    },
    RemovePackage: (state, action: PayloadAction<number>) => {
      const index = state.packageList.findIndex((x) => x.id === action.payload);
      state.packageList = state.packageList.filter((x) => x.id !== action.payload);
      if (index === state.selectedPackageIndex) state.selectedPackageIndex = -1;
      if (index < state.selectedPackageIndex && state.packageList.length > 0)
        state.selectedPackageIndex = state.selectedPackageIndex - 1;
    },

    ChangeMetadata: (
      state,
      action: PayloadAction<{
        id: number;
        value: any;
        valueIndex?: number;
        pushAndReplaceWhereCultureIds?: number[];
      }>
    ) => {
      const { id, value, valueIndex, pushAndReplaceWhereCultureIds } = action.payload;

      let match = state.metadata.find((x) => x.field?.id === id)!.value!;
      if (valueIndex === undefined) {
        if (pushAndReplaceWhereCultureIds === undefined) match.value = value;
        else {
          // Considering the "cultureId" prop never changes
          match.value = match.value.filter((x) => !pushAndReplaceWhereCultureIds.includes(x.cultureId));
          value.forEach((element) => {
            match.value.push(element);
          });
        }
      } else match.value[valueIndex] = value;
    },
    ChangeArtworkMetadata: (state, action: PayloadAction<{ artworkId: number; metadata: DbAssetObjectInfos }>) => {
      state.artworkMeta = state.artworkMeta.map((x) => {
        if (x.id === action.payload.artworkId)
          return {
            ...x,
            metadata: [...x.metadata.filter((m) => m.id !== action.payload.metadata.id), action.payload.metadata],
          };
        else return x;
      });
    },
    AddTitleMetadata: (state, action: PayloadAction<{ metadata: EnrichedMetadata[]; cultureIds: number[] }>) => {
      const { metadata, cultureIds } = action.payload;

      if (state.titleLocalizedMeta.length < 1) {
        // Ensure we get only requested culture's data
        state.titleLocalizedMeta = metadata.map((x) =>
          x.value &&
          x.value.value &&
          x.value.value.length > 0 &&
          (x.value.value[0].cultureId > 0 ||
            x.value.value[0].languageLocalizedCultureId > 0 ||
            (x.value.value[0].system && x.value.value[0].system.cultureId > 0))
            ? {
                ...x,
                value: {
                  ...x.value,
                  value: [
                    ...x.value.value.filter((v) =>
                      cultureIds.find(
                        (c) =>
                          c === v.languageLocalizedCultureId ||
                          c === v.cultureId ||
                          (v.system && c === v.system.cultureId)
                      )
                    ),
                  ],
                },
              }
            : x
        );
        return;
      }

      for (let i = 0; i < metadata.length; i++) {
        let targetMetadata = state.titleLocalizedMeta.find((x) => x.field?.id === metadata[i].field?.id);
        // Only consider localized metadata
        if (
          targetMetadata &&
          targetMetadata.value?.value &&
          targetMetadata.value.value.length > 0 &&
          (targetMetadata.value?.value[0].cultureId > 0 ||
            targetMetadata.value?.value[0].languageLocalizedCultureId > 0 ||
            (targetMetadata.value.value[0].system && targetMetadata.value.value[0].system.cultureId > 0))
        ) {
          // keep only requested cultures metadata
          const metadataValuesToAdd = metadata[i].value?.value.reduce((acc, x) => {
            if (
              cultureIds.find(
                (c) => c === x.languageLocalizedCultureId || c === x.cultureId || (x.system && c === x.system.cultureId)
              )
            ) {
              return [...acc, x];
            } else return acc;
          }, []);

          // Add kept data values to existing metadata
          targetMetadata.value.value = [
            ...targetMetadata.value.value.filter(
              (x) =>
                !cultureIds.find(
                  (c) =>
                    c === x.languageLocalizedCultureId || c === x.cultureId || (x.system && c === x.system.cultureId)
                )
            ),
            ...metadataValuesToAdd,
          ];
        }
      }
    },

    AddMetadata: (state, action: PayloadAction<{ metadata: EnrichedMetadata[]; cultureIds: number[] }>) => {
      const { metadata, cultureIds } = action.payload;

      if (!metadata || metadata.length < 1) return;

      if (state.metadata.length < 1) {
        // Ensure we get only requested culture's data
        state.metadata = metadata.map((x) =>
          x.value &&
          x.value.value &&
          x.value.value.length > 0 &&
          (x.value.value[0].cultureId > 0 || x.value.value[0].languageLocalizedCultureId > 0)
            ? {
                ...x,
                value: {
                  ...x.value,
                  value: [
                    ...x.value.value.filter((v) =>
                      cultureIds.find((c) => c === v.languageLocalizedCultureId || c === v.cultureId)
                    ),
                  ],
                },
              }
            : x
        );
        return;
      }

      for (let i = 0; i < metadata.length; i++) {
        let targetMetadata = state.metadata.find((x) => x.field?.id === metadata[i].field?.id);
        if (
          targetMetadata &&
          targetMetadata.value?.value &&
          targetMetadata.value.value.length > 0 &&
          (targetMetadata.value?.value[0].cultureId > 0 ||
            targetMetadata.value?.value[0].languageLocalizedCultureId > 0)
        ) {
          const metadataValuesToAdd = metadata[i].value?.value.reduce((acc, x) => {
            if (cultureIds.find((c) => c === x.languageLocalizedCultureId || c === x.cultureId)) {
              return [...acc, x];
            } else return acc;
          }, []);

          targetMetadata.value.value = [
            ...targetMetadata.value.value.filter(
              (x) => !cultureIds.find((c) => c === x.languageLocalizedCultureId || c === x.cultureId)
            ),
            ...metadataValuesToAdd,
          ];
        }
      }
    },
    ChangePackageName: (state, action: PayloadAction<{ name: string; packageId: number }>) => {
      const { name, packageId } = action.payload;
      const foundPackage = state.packageList.find((x) => x.id === packageId);
      if (foundPackage) foundPackage.name = name;
    },

    CancelEdition: (state) => {
      if (state.packageList && state.packageList.length > 0 && state.selectedPackageIndex >= 0) {
        delete state.metadataMap[state.packageList[state.selectedPackageIndex].id];
        state.statusMap[state.packageList[state.selectedPackageIndex].id] = false;
      }
      state.selectedPackageIndex = -1;
      state.selectedCultureIndex = -1;
      state.metadata = [];
    },

    InitBundleMaker: (state) => {
      return {
        ...initialState,
        cultures: state.cultures,
        broadcasters: state.broadcasters,
      };
    },
    CopyMetadata: (
      state,
      action: PayloadAction<{
        fieldIds: number[];
        targetCultureIds: number[];
        sourceCultureId: number;
        packageToCopy: number;
      }>
    ) => {
      const { fieldIds, sourceCultureId, targetCultureIds, packageToCopy } = action.payload;

      if (!fieldIds || fieldIds.length < 1 || !sourceCultureId || !targetCultureIds || targetCultureIds.length < 1)
        return;

      if (state.packageList[state.selectedPackageIndex].id === packageToCopy) {
        state.metadata = generateCopyMetadata(
          fieldIds,
          sourceCultureId,
          targetCultureIds,
          state.metadata,
          state.metadata,
          state.cultures
        );
      } else {
        state.metadataMap[packageToCopy] = generateCopyMetadata(
          fieldIds,
          sourceCultureId,
          targetCultureIds,
          state.metadataMap[packageToCopy],
          state.metadata,
          state.cultures
        );
      }

      state.statusMap[packageToCopy] = true;
    },
    CopyTitleMetadata: (
      state,
      action: PayloadAction<{ fieldIds: number[]; targetCultureIds: number[]; sourceCultureId: number }>
    ) => {
      const { fieldIds, targetCultureIds, sourceCultureId } = action.payload;
      if (!fieldIds || !targetCultureIds || !sourceCultureId || !state.selectedTitle?.id) return;

      state.titleLocalizedMeta = generateCopyMetadata(
        fieldIds,
        sourceCultureId,
        targetCultureIds,
        state.titleLocalizedMeta,
        state.titleLocalizedMeta,
        state.cultures
      );

      state.statusMap[state.selectedTitle.id] = true;
    },

    HandleCreatedPackage: (
      state,
      action: PayloadAction<{
        selectedCultures: DbCultureInfos[];
        broadcasterId: number;
        createdObject: DbAssetObjectInfos;
      }>
    ) => {
      const { selectedCultures, broadcasterId, createdObject } = action.payload;
      if (state.selectedPackageIndex >= 0 && state.packageList.length > 0) {
        state.metadataMap[state.packageList[state.selectedPackageIndex].id] = state.metadata;
      }
      state.metadata = [];
      state.packageCultures = selectedCultures;
      state.selectedCultureIndex = 0;
      state.packageBroadcasterId = broadcasterId;
      state.selectedPackageIndex = [...state.packageList, createdObject]
        .sort((a, b) => b.id - a.id)
        .findIndex((x) => x.id === createdObject.id);
      state.packageList = [...state.packageList, createdObject].sort((a, b) => b.id - a.id);
      state.statusMap[createdObject.id] = true;
      state.editTitle = false;
    },
    HandlePackageChange: (state, action: PayloadAction<{ foundCultures: DbCultureInfos[]; packageIndex: number }>) => {
      const { foundCultures, packageIndex } = action.payload;
      state.metadata = [];
      state.selectedPackageIndex = packageIndex;
      state.packageCultures = foundCultures;
      if (foundCultures.length > 0) state.selectedCultureIndex = 0;
    },
    HandleChangeArtworkMetadata: (
      state,
      action: PayloadAction<{ artworkId: number; metadata: IDbBaseMetadataValueInfos[] }>
    ) => {
      const { artworkId, metadata } = action.payload;
      const index = state.artworkMeta.findIndex((x) => x.id === artworkId);
      state.artworkMeta[index].metadata = [
        ...state.artworkMeta[index].metadata.filter((x) => !metadata.find((m) => m.id === x.id)),
        ...metadata,
      ];
    },
    HydrateMetadataFromTitle: (
      state,
      action: PayloadAction<{ selectedCulturesTitle: number[]; selectedFieldsTitle: number[] }>
    ) => {
      const { selectedCulturesTitle, selectedFieldsTitle } = action.payload;

      state.metadata = state.metadata.map((m) => {
        const correspondingTitleMeta = state.titleLocalizedMeta.find((t) => t.field?.id === m.field?.id);
        if (
          selectedFieldsTitle.find((f) => f === m.field?.id) &&
          correspondingTitleMeta &&
          correspondingTitleMeta.value &&
          correspondingTitleMeta.value.value &&
          correspondingTitleMeta.value.value.length > 0 &&
          m.value
        ) {
          const newValues = state.titleLocalizedMeta
            .find((t) => t.field?.id === m.field?.id)
            ?.value?.value.filter((v) =>
              selectedCulturesTitle.find((c) => c === v.languageLocalizedCultureId || c === v.cultureId)
            );
          return {
            ...m,
            value: {
              ...m.value,
              value: [
                ...m.value?.value.filter(
                  (v) => !selectedCulturesTitle.find((c) => c === v.languageLocalizedCultureId || c === v.cultureId)
                ),
                ...newValues,
              ],
            },
          };
        } else {
          return m;
        }
      });

      state.statusMap[state.packageList[state.selectedPackageIndex].id] = true;
      state.fieldUpdate = {
        field: "all",
        count: state.fieldUpdate.count + 1,
      };
    },
    UpdateCastRows: (state, action) => {
      const { value, index, field } = action.payload;
      let baseMeta = state.editTitle ? state.titleLocalizedMeta : state.metadata;
      const currentCultureId = state.editTitle
        ? state.titleSelectedCulture?.id ?? GlobalConst.WORLD_CULTURE_ID
        : state.packageCultures[state.selectedCultureIndex].id;

      let languageValue =
        field === "language"
          ? baseMeta
              .find(
                (x) =>
                  x.field!.id ===
                  cacheService.GetMetadata().find((y) => y.baseType === BaseMetadataType.DbCastsMetadataValueInfos)!.id
              )
              ?.value?.value.find(
                (v) =>
                  v.languageLocalized.id && (v.languageLocalized.id === value || v.languageLocalized.id === value.id)
              ).languageLocalized
          : null;

      const newMeta = baseMeta.map((x) => {
        if (
          x.field!.id ===
          cacheService.GetMetadata().find((y) => y.baseType === BaseMetadataType.DbCastsMetadataValueInfos)!.id
        ) {
          return {
            ...x,
            value: {
              ...x.value!,
              id: x.value!.id,
              value: x.value!.value.map((v, i) => {
                if (v.languageLocalizedCultureId === currentCultureId && i > index) {
                  switch (field) {
                    case "name":
                      return { ...v, name: value };
                    case "charName":
                      return { ...v, charName: value };
                    case "functionEnum":
                      return { ...v, functionEnum: value };
                    case "lastname":
                      return { ...v, lastname: value };
                    case "language":
                      return { ...v, languageLocalized: languageValue };

                    default:
                      return v;
                  }
                } else return v;
              }),
            },
          };
        } else {
          return x;
        }
      });
      if (state.editTitle) state.titleLocalizedMeta = newMeta;
      else state.metadata = newMeta;
      const newFieldUpdate = state.editTitle ? "title_cast" : "cast";
      state.fieldUpdate = {
        field: newFieldUpdate,
        count: state.fieldUpdate.field === newFieldUpdate ? state.fieldUpdate.count + 1 : 1,
      };
    },
    SetUpdateField: (state, action) => {
      state.fieldUpdate = {
        field: action.payload.field,
        count: state.fieldUpdate.field === action.payload.field ? state.fieldUpdate.count + 1 : 1,
      };
    },
    RemoveCastRow: (state, action) => {
      let baseMeta = state.editTitle ? state.titleLocalizedMeta : state.metadata;
      const castMeta = baseMeta.filter((x) => x?.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos);
      const packageId =
        state.packageList.length > 0 &&
        state.selectedPackageIndex >= 0 &&
        state.packageList[state.selectedPackageIndex].id;
      if (!packageId && !state.editTitle) return;

      const selectedCultureId = state.editTitle
        ? state.titleSelectedCulture?.id ?? GlobalConst.WORLD_CULTURE_ID
        : state.packageCultures.length > 0 &&
          state.selectedCultureIndex >= 0 &&
          state.packageCultures[state.selectedCultureIndex];
      if (castMeta[0] && castMeta[0].value?.value && selectedCultureId) {
        const toRemove = castMeta[0].value?.value.find((x, i) => i === action.payload.index);
        const isLastOfCultureAndEpisode =
          castMeta[0].value?.value.filter(
            (x, i) =>
              x?.epNb === toRemove?.epNb && x?.languageLocalizedCultureId === toRemove?.languageLocalizedCultureId
          ).length <= 1;
        const filteredCastValues = castMeta[0].value?.value.filter((x, i) => i !== action.payload.index);
        const newValue = isLastOfCultureAndEpisode
          ? [
              ...filteredCastValues,
              { ...toRemove, lastname: null, name: "delete", function: null, functionEnum: null, order: 9999 },
            ]
          : filteredCastValues;
        const newCastMeta = {
          ...castMeta[0],
          value: { ...castMeta[0].value, value: newValue },
        };
        const newMeta = baseMeta.map((x) =>
          x.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos ? newCastMeta : x
        );
        if (state.editTitle) state.titleLocalizedMeta = newMeta;
        else state.metadata = newMeta;

        const newFieldUpdate = state.editTitle ? "title_cast" : "cast";
        state.fieldUpdate = {
          field: newFieldUpdate,
          count: state.fieldUpdate.field === newFieldUpdate ? state.fieldUpdate.count + 1 : 1,
        };
        if (!state.editTitle && packageId) state.statusMap[packageId] = true;
      }
    },
    AddCastRow: (state, action) => {
      let baseMeta = state.editTitle ? state.titleLocalizedMeta : state.metadata;
      const castMeta = baseMeta.filter((x) => x?.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos);
      const packageId =
        state.packageList.length > 0 &&
        state.selectedPackageIndex >= 0 &&
        state.packageList[state.selectedPackageIndex].id;
      if (
        (!packageId && !state.editTitle) ||
        !castMeta ||
        !castMeta[0] ||
        !castMeta[0].value ||
        !castMeta[0].value.value
      )
        return;

      const language = state.editTitle ? state.titleSelectedCulture : state.packageCultures[state.selectedCultureIndex];

      const newValue = {
        function: null,
        order: 1,
        functionEnum: null,
        lastname: null,
        name: null,
        charName: null,
        languageLocalizedCultureId: language ? language.id : null,
        languageLocalized: language?.languages?.find((l) => l.isMain),
        languageCode: null,
        epNb: state.editTitle ? state.selectedEpisode : action.payload.epNb ?? null,
      };
      const newCastMeta = {
        ...castMeta[0],
        value: {
          ...castMeta[0].value!,
          value:
            castMeta[0].value!.value && castMeta[0].value!.value.length > 0
              ? [
                  ...castMeta[0].value!.value.filter(
                    (x) =>
                      !(
                        x.epNb === state.selectedEpisode &&
                        x.languageLocalizedCultureId === (language ? language.id : null) &&
                        x.order === 9999
                      )
                  ),
                  newValue,
                ]
              : [newValue],
        },
      };

      const newMeta = baseMeta.map((x) =>
        x.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos ? newCastMeta : x
      );
      if (state.editTitle) state.titleLocalizedMeta = newMeta;
      else state.metadata = newMeta;
      const newFieldUpdate = state.editTitle ? "title_cast" : "cast";
      state.fieldUpdate = {
        field: newFieldUpdate,
        count: state.fieldUpdate.field === newFieldUpdate ? state.fieldUpdate.count + 1 : 1,
      };
      if (!state.editTitle && packageId) state.statusMap[packageId] = true;
    },
    DuplicateCastRow: (state, action) => {
      let baseMeta = state.editTitle ? state.titleLocalizedMeta : state.metadata;
      const castMeta = baseMeta.filter((x) => x?.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos);
      const packageId =
        state.packageList.length > 0 &&
        state.selectedPackageIndex >= 0 &&
        state.packageList[state.selectedPackageIndex].id;
      if ((!packageId && !state.editTitle) || !castMeta || !castMeta[0] || !castMeta[0].value) return;

      const newCastMeta = {
        ...castMeta[0],
        value: {
          ...castMeta[0].value,
          value: [
            ...castMeta[0].value.value,
            ...castMeta[0].value.value.filter((x, i) => action.payload.indexes.find((y) => y === i)),
          ],
        },
      };
      const newMeta = baseMeta.map((x) =>
        x.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos ? newCastMeta : x
      );
      if (state.editTitle) state.titleLocalizedMeta = newMeta;
      else state.metadata = newMeta;
      const newFieldUpdate = state.editTitle ? "title_cast" : "cast";
      state.fieldUpdate = {
        field: newFieldUpdate,
        count: state.fieldUpdate.field === newFieldUpdate ? state.fieldUpdate.count + 1 : 1,
      };
      if (!state.editTitle && packageId) state.statusMap[packageId] = true;
    },
  },
});

export const {
  SetOpenTitleId,
  SetBundleId,
  SetMetadata,
  SetPackageBroadcasterId,
  SetPackageCultures,
  SetPackageList,
  SetSelectedCultureIndex,
  SetSelectedPackageIndex,
  SetArtworks,
  AddCultures,
  AddPackage,
  AddArtwork,
  RemoveCulture,
  RemoveTitleCulture,
  RemovePackage,
  ChangeMetadata,
  ChangeArtworkMetadata,
  AddMetadata,
  ChangePackageName,
  SetSelectedTitle,
  SetTitleLocalizedMeta,
  UpdateTitleLocalizedMeta,
  SetBroadcasters,
  SetCultures,
  SetEditTitle,
  SetIsRightPanelOpen,
  CancelEdition,
  InitBundleMaker,
  CopyMetadata,
  HandleCreatedPackage,
  HandlePackageChange,
  HandleChangeArtworkMetadata,
  SetMetadataMap,
  SetArtworkContentId,
  RemoveArtwork,
  SetTitleCultures,
  AddTitleCultures,
  AddTitleMetadata,
  SetTitleSelectedCulture,
  CopyTitleMetadata,
  HydrateMetadataFromTitle,
  SetStatusMap,
  UpdateCastRows,
  SetUpdateField,
  RemoveCastRow,
  AddCastRow,
  DuplicateCastRow,
  SetSelectedEpisode,
  SetCurrentPackageStatus,
} = bundleMakerSlice.actions;

export const GetSelectedTitle = (state: RootState): DbAssetObjectInfos | null => state.bundleMakerSlice.selectedTitle;
export const GetBundleId = (state: RootState): number => state.bundleMakerSlice.bundleId;
export const GetMetadata = (state: RootState): EnrichedMetadata[] => state.bundleMakerSlice.metadata;
export const GetPackageBroadcasterId = (state: RootState): number => state.bundleMakerSlice.packageBroadcasterId;
export const GetPackageCultures = (state: RootState): DbCultureInfos[] => state.bundleMakerSlice.packageCultures;
export const GetPackageList = (state: RootState): DbAssetObjectInfos[] => state.bundleMakerSlice.packageList;
export const GetSelectedCultureIndex = (state: RootState): number => state.bundleMakerSlice.selectedCultureIndex;
export const GetSelectedCulture = (state: RootState): DbCultureInfos | null =>
  state.bundleMakerSlice.packageCultures[state.bundleMakerSlice.selectedCultureIndex];
export const GetSelectedPackageIndex = (state: RootState): number => state.bundleMakerSlice.selectedPackageIndex;
export const GetBroadcasterList = (state: RootState): DbDeliveryBroadcasterInfos[] =>
  state.bundleMakerSlice.broadcasters;
export const GetCulturesList = (state: RootState): DbCultureInfos[] => state.bundleMakerSlice.cultures;
export const GetMetadataMap = (state: RootState) => state.bundleMakerSlice.metadataMap;
export const GetArtworkMeta = (state: RootState) => state.bundleMakerSlice.artworkMeta;
export const GetArtworkContentId = (state: RootState) => state.bundleMakerSlice.artworkContentId;
export const GetEditTitle = (state: RootState) => state.bundleMakerSlice.editTitle;
export const GetIsRightPanelOpen = (state: RootState) => state.bundleMakerSlice.isRightPanelOpen;
export const GetTitleLocalizedMeta = (state: RootState) => state.bundleMakerSlice.titleLocalizedMeta;
export const GetTitleCultures = (state: RootState) => state.bundleMakerSlice.titleCultures;
export const GetTitleSelectedCulture = (state: RootState) => state.bundleMakerSlice.titleSelectedCulture;
export const GetStatusMap = (state: RootState) => state.bundleMakerSlice.statusMap;
export const GetOpenTitleId = (state: RootState) => state.bundleMakerSlice.openTitleId;
export const GetFieldUpdate = (state: RootState) => state.bundleMakerSlice.fieldUpdate;
export const GetSelectedEpisode = (state: RootState) => state.bundleMakerSlice.selectedEpisode;

const generateCopyMetadata = (
  fieldIds: number[],
  sourceCultureId: number,
  targetCultureIds: number[],
  metadata: any,
  sourceMetadata: any,
  cultures: DbCultureInfos[]
) =>
  metadata.map((x) => {
    if (
      fieldIds.find((y) => y === x.field?.id) &&
      x.value &&
      x.value.value &&
      sourceMetadata.find((sm) => fieldIds.find((y) => y === x.field?.id) === sm.field?.id)
    ) {
      let sourceVal = sourceMetadata
        .find((sm) => fieldIds.find((y) => y === x.field?.id) === sm.field?.id)
        .value.value.filter((v) => v.languageLocalizedCultureId === sourceCultureId || v.cultureId === sourceCultureId);
      let valuesToAdd: any = [];
      // Create a new value for each source value for each target culture
      targetCultureIds.forEach((c) => {
        sourceVal.forEach((s) => {
          let value = { ...s };
          if (s.languageLocalizedCultureId) value.languageLocalizedCultureId = c;
          if (s.language)
            value.language = cultures?.find((a) => a.id === c)?.languages?.find((l) => l.isMain)?.isoCodeLocalized;
          if (s.languageLocalized) {
            const targetCulture = cultures?.find((a) => a.id === c);
            value.languageLocalized = {
              ...targetCulture?.languages?.find((l) => l.isMain),
            };
          }
          if (s.languageCode) {
            const targetCulture = cultures?.find((a) => a.id === c);
            if (targetCulture?.languagesMap) value.languageCode = { ...targetCulture!.languagesMap[0] };
          }
          if (s.cultureId) value.cultureId = c;
          valuesToAdd.push(value);
        });
      });
      let newValues = [
        // filter out target cultures existing values
        ...x.value.value.filter(
          (v) =>
            !targetCultureIds.find(
              (c) => c === v.languageLocalizedCultureId || targetCultureIds.find((c) => c === v.cultureId)
            )
        ),
        ...valuesToAdd,
      ];
      return { ...x, value: { ...x.value, value: newValues } };
    } else {
      return x;
    }
  });

const convertTypeToValueType = (fieldType: BaseMetadataType) => {
  switch (fieldType) {
    case BaseMetadataType.DbLocalizedBoolMultiMetadataValueInfos:
      return `VDM.AirLab.Sdk.Core.Types.CommonTypes.LocalizedBoolMetadataInfos, VDM.AirLab.Sdk.Core`;
    case BaseMetadataType.DbLocalizedDateTimeMultiMetadataValueInfos:
      return `VDM.AirLab.Sdk.Core.Types.CommonTypes.LocalizedDateTimeMetadataInfos, VDM.AirLab.Sdk.Core`;
    case BaseMetadataType.DbLocalizedCreditsMultiMetadataValueInfos:
      return "VDM.AirLab.Sdk.Core.Types.CommonTypes.LocalizedCreditsMetadataInfos, VDM.AirLab.Sdk.Core";
    case BaseMetadataType.DbCastsMetadataValueInfos:
      return "VDM.AirLab.Sdk.Core.Types.AssetTypes.CastMetadataInfos, VDM.AirLab.Sdk.Core";
    case BaseMetadataType.DbLocalizedRatingMultiMetadataValueInfos:
      return "VDM.AirLab.Sdk.Core.Types.RatingTypes.RatingMetadataInfos, VDM.AirLab.Sdk.Core";
    case BaseMetadataType.DbSynopsesMetadataValueInfos:
      return "VDM.AirLab.Sdk.Core.Types.AssetTypes.SynopsisMetadataInfos, VDM.AirLab.Sdk.Core";
    case BaseMetadataType.DbLocalizedTitlesMultiMetadataValueInfos:
      return "VDM.AirLab.Sdk.Core.Types.CommonTypes.LocalizedTitleMetadataInfos, VDM.AirLab.Sdk.Core";
    default:
      return `VDM.AirLab.Sdk.Core.Types.CommonTypes.${fieldType}, VDM.AirLab.Sdk.Core`;
  }
};

export default bundleMakerSlice.reducer;
